import React, { useEffect, useMemo, useState } from 'react';
import './App.css';

function App() {
  const [splashStage, setSplashStage] = useState<'waiting' | 'slamming' | 'fading' | 'hidden'>('waiting');

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const [leftDoorClosed, setLeftDoorClosed] = useState(false);
  const [rightDoorClosed, setRightDoorClosed] = useState(false);

  const handleDoorClick = (side: 'left' | 'right') => {
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setSplashStage('hidden');
      return;
    }

    if (side === 'left') {
      setLeftDoorClosed(true);
    } else {
      setRightDoorClosed(true);
    }

    // Play door slam sound
    const audio = new Audio('/door-slam.mp3');
    audio.volume = 0.4;
    audio.play().catch((err) => {
      console.log('Audio playback failed:', err);
    });
  };

  const handleSignClick = () => {
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setSplashStage('hidden');
      return;
    }

    // Slam both doors at once
    setLeftDoorClosed(true);
    setRightDoorClosed(true);

    // Play door slam sound (slightly louder for both)
    const audio = new Audio('/door-slam.mp3');
    audio.volume = 0.6;
    audio.play().catch((err) => {
      console.log('Audio playback failed:', err);
    });
  };

  useEffect(() => {
    if (leftDoorClosed && rightDoorClosed) {
      const fadeTimer = window.setTimeout(() => setSplashStage('fading'), 600);
      const hideTimer = window.setTimeout(() => setSplashStage('hidden'), 1200);
      return () => {
        window.clearTimeout(fadeTimer);
        window.clearTimeout(hideTimer);
      };
    }
  }, [leftDoorClosed, rightDoorClosed]);

  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setSplashStage('hidden');
    }
  }, []);

  return (
    <div className="stfdc-app">
      {splashStage !== 'hidden' && (
        <div className={`splash ${splashStage}`} aria-hidden>
          {splashStage === 'waiting' && (
            <div className="door-sign" onClick={handleSignClick}>
              <div className="sign-board">
                <span className="sign-text">Slam Those Doors</span>
                <span className="sign-subtext">To Enter</span>
              </div>
            </div>
          )}
          <div className="splash-inner">
            <div className="cancer" aria-hidden>
              <div className="cancer-cell-wrap">
                <svg className="cancer-cell-svg" viewBox="0 0 140 140" aria-hidden>
                  <defs>
                    <radialGradient id="cellGradient" cx="35%" cy="35%" r="65%">
                      <stop offset="0%" stopColor="#e9d5ff" />
                      <stop offset="50%" stopColor="#c084fc" />
                      <stop offset="85%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#6b21a8" />
                    </radialGradient>
                    <radialGradient id="nucleusGrad" cx="40%" cy="40%" r="60%">
                      <stop offset="0%" stopColor="#f3e8ff" />
                      <stop offset="60%" stopColor="#d8b4fe" />
                      <stop offset="100%" stopColor="#9333ea" />
                    </radialGradient>
                    <radialGradient id="organelleGrad" cx="30%" cy="30%" r="70%">
                      <stop offset="0%" stopColor="#e9d5ff" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </radialGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  <g filter="url(#glow)">
                    <circle className="cell-glow" cx="70" cy="70" r="48" fill="#a855f7" opacity="0.3" />
                    
                    <circle className="cell-body" cx="70" cy="70" r="42" fill="url(#cellGradient)" stroke="#581c87" strokeWidth="2.5" />
                    
                    <g className="cell-membrane" opacity="0.15">
                      <circle cx="70" cy="70" r="38" fill="none" stroke="#e9d5ff" strokeWidth="1" strokeDasharray="3 2" />
                      <circle cx="70" cy="70" r="34" fill="none" stroke="#e9d5ff" strokeWidth="0.5" />
                    </g>
                    
                    <g className="organelles">
                      <circle cx="55" cy="58" r="6" fill="url(#organelleGrad)" opacity="0.6" />
                      <circle cx="80" cy="62" r="5" fill="url(#organelleGrad)" opacity="0.5" />
                      <circle cx="65" cy="78" r="4" fill="url(#organelleGrad)" opacity="0.7" />
                      <circle cx="82" cy="80" r="3.5" fill="url(#organelleGrad)" opacity="0.5" />
                      <circle cx="58" cy="75" r="3" fill="url(#organelleGrad)" opacity="0.6" />
                    </g>
                    
                    <circle className="nucleus" cx="70" cy="70" r="16" fill="url(#nucleusGrad)" stroke="#6b21a8" strokeWidth="1.5" />
                    <ellipse cx="65" cy="65" rx="5" ry="7" fill="#f3e8ff" opacity="0.4" />
                    
                    <g className="spikes">
                      <ellipse cx="70" cy="26" rx="5" ry="7" fill="#a855f7" transform="rotate(-10 70 26)" />
                      <ellipse cx="93" cy="32" rx="4" ry="6" fill="#a855f7" transform="rotate(20 93 32)" />
                      <ellipse cx="108" cy="50" rx="4.5" ry="6.5" fill="#a855f7" transform="rotate(45 108 50)" />
                      <ellipse cx="112" cy="75" rx="5" ry="7" fill="#a855f7" transform="rotate(80 112 75)" />
                      <ellipse cx="105" cy="100" rx="4" ry="6" fill="#a855f7" transform="rotate(110 105 100)" />
                      <ellipse cx="85" cy="112" rx="5" ry="7" fill="#a855f7" transform="rotate(140 85 112)" />
                      <ellipse cx="70" cy="114" rx="4.5" ry="6.5" fill="#a855f7" transform="rotate(170 70 114)" />
                      <ellipse cx="55" cy="112" rx="4" ry="6" fill="#a855f7" transform="rotate(-140 55 112)" />
                      <ellipse cx="35" cy="100" rx="5" ry="7" fill="#a855f7" transform="rotate(-110 35 100)" />
                      <ellipse cx="28" cy="75" rx="4.5" ry="6.5" fill="#a855f7" transform="rotate(-80 28 75)" />
                      <ellipse cx="32" cy="50" rx="4" ry="6" fill="#a855f7" transform="rotate(-45 32 50)" />
                      <ellipse cx="47" cy="32" rx="5" ry="7" fill="#a855f7" transform="rotate(-20 47 32)" />
                    </g>
                    
                    <g className="spike-highlights" opacity="0.4">
                      <ellipse cx="70" cy="28" rx="2" ry="3" fill="#e9d5ff" />
                      <ellipse cx="93" cy="34" rx="1.5" ry="2.5" fill="#e9d5ff" />
                      <ellipse cx="106" cy="52" rx="2" ry="3" fill="#e9d5ff" />
                      <ellipse cx="110" cy="75" rx="2" ry="3" fill="#e9d5ff" />
                    </g>
                  </g>
                </svg>
              </div>
            </div>
            <div className="door">
              <div 
                className={`door-left ${leftDoorClosed ? 'closed' : ''}`}
                onClick={() => !leftDoorClosed && handleDoorClick('left')}
                style={{ cursor: leftDoorClosed ? 'default' : 'pointer' }}
              >
                <div className="door-wood">
                  <div className="door-panels">
                    <span className="panel"></span>
                    <span className="panel"></span>
                    <span className="panel"></span>
                    <span className="panel"></span>
                  </div>
                </div>
                <span className="handle handle-left"></span>
                <div className="hinges hinges-left" aria-hidden>
                  <span></span><span></span><span></span>
                </div>
              </div>
              <div 
                className={`door-right ${rightDoorClosed ? 'closed' : ''}`}
                onClick={() => !rightDoorClosed && handleDoorClick('right')}
                style={{ cursor: rightDoorClosed ? 'default' : 'pointer' }}
              >
                <div className="door-wood">
                  <div className="door-panels">
                    <span className="panel"></span>
                    <span className="panel"></span>
                    <span className="panel"></span>
                    <span className="panel"></span>
                  </div>
                </div>
                <span className="handle handle-right"></span>
                <div className="hinges hinges-right" aria-hidden>
                  <span></span><span></span><span></span>
                </div>
              </div>
              <div className="door-seam" aria-hidden></div>
            </div>
            <div className="splash-title">Shut The Front Door To Cancer</div>
          </div>
        </div>
      )}
      <header className="site-header" role="banner">
        <div className="container header-inner">
          <div className="brand">
            <span aria-hidden>🎗️</span>
            <span className="brand-text">Shut The Front Door To Cancer</span>
          </div>
          <nav className="nav" aria-label="Primary">
            <a
              href="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
              className="nav-link"
              zeffy-form-link="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
            >
              Donate
            </a>
            <a href="#tickets" className="nav-link">Tickets</a>
            <a href="#about" className="nav-link">About</a>
          </nav>
        </div>
      </header>

      <main id="main" className="site-main" role="main">
        <section className="hero">
          <div className="container hero-inner">
            <div className="hero-badge">The Lynch Family Presents</div>
            <h1 className="hero-title">Shut The Front Door To Cancer</h1>
            <p className="hero-subtitle">Join us for a fun filled night celebrating Jan Lynch!</p>

            <div className="cta-group">
              <a
                id="donate"
                href="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
                className="btn btn-primary"
                zeffy-form-link="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
              >
                Donate Now
              </a>
              <a
                id="tickets"
                href="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true"
                className="btn btn-accent"
                zeffy-form-link="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true"
              >
                Register for Event
              </a>
            </div>

            <p className="hero-note">Proceeds benefit Lightways Hospice Care and American Cancer Society: HPV Prevention, Research, and Awareness</p>
          </div>
          <div className="hero-gradient" aria-hidden></div>
        </section>

        <section className="event" aria-labelledby="event-heading">
          <div className="container event-inner">
            <h2 id="event-heading" className="section-title">Event Details</h2>
            <div className="event-grid">
              <div className="event-card">
                <div className="event-meta">
                  <div className="meta-item">
                    <span className="meta-label">Location</span>
                    <span className="meta-value">115 Bourbon Street</span>
                    <span className="meta-subvalue">3359 W 115th Street, Merrionette Park, IL</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Date & Time</span>
                    <span className="meta-value">June 13th, 2026</span>
                    <span className="meta-subvalue">5:00 PM - 9:00 PM</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Registration</span>
                    <span className="meta-value">$45 per person</span>
                    <span className="meta-subvalue">Before June 1st</span>
                  </div>
                </div>
                <div className="pricing-note">
                  <strong>$55 per person</strong> at the door • Dinner, beer and wine included
                </div>
                <p className="event-copy">Join us for an unforgettable evening celebrating Jan Lynch! Come together with friends, family, and community for a night filled with delicious food, great music, exciting raffles, and heartfelt moments. Your participation directly supports Lightways Hospice Care and American Cancer Society's vital work in HPV prevention, research, and awareness.</p>
                <div className="cta-row">
                  <a className="btn btn-accent" href="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true" zeffy-form-link="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true">Register Now</a>
                  <a
                    className="btn btn-ghost"
                    href="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
                    zeffy-form-link="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
                  >
                    Donate
                  </a>
                </div>
              </div>
              <div className="event-side">
                <h3 className="benefits-title">What's Included</h3>
                <ul className="benefits">
                  <li>🍽️ Dinner with beer and wine</li>
                  <li>🎵 DJ and dancing</li>
                  <li>🎁 Exciting raffles</li>
                  <li>🍹 Cash bar for drinks</li>
                  <li>❤️ Celebration of Jan Lynch</li>
                </ul>
                <div className="beneficiaries">
                  <h3 className="benefits-title">Supporting</h3>
                  <div className="beneficiary-item" onClick={() => window.open('https://lightways.org/hospice-care/', '_blank')}>
                    <img src="/lightways.webp" alt="Lightways Hospice Care" className="beneficiary-logo" />
                    <div className="beneficiary-text">
                      <strong>Lightways Hospice Care</strong>
                      <span>Founded as Joliet Area Community Hospice</span>
                    </div>
                  </div>
                  <div className="beneficiary-item" onClick={() => window.open('https://www.cancer.org/cancer.html', '_blank')}>
                    <img src="/american_cancer_society.svg" alt="American Cancer Society" className="beneficiary-logo" />
                    <div className="beneficiary-text">
                      <strong>American Cancer Society</strong>
                      <span>HPV Prevention, Research, and Awareness</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="about" aria-labelledby="about-heading">
          <div className="container about-inner">
            <h2 id="about-heading" className="section-title">Celebrating Jan Lynch</h2>
            <p>Join us in honoring the memory and spirit of Jan Lynch. This fundraiser brings together family, friends, and community to celebrate her life while making a meaningful difference in the fight against cancer. Through your support, we're helping fund crucial research, prevention programs, and compassionate hospice care for those who need it most.</p>
            <p>Together, we can shut the front door to cancer—one donation, one celebration, one act of kindness at a time.</p>
            
            <div className="photo-gallery">
              <div className="photo-card">
                <img src="/IMG_1301.jpg" alt="Jan Lynch smiling" className="photo-img" />
              </div>
              <div className="photo-card">
                <img src="/IMG_1892.jpg" alt="Jan Lynch with loved ones" className="photo-img" />
              </div>
              <div className="photo-card">
                <img src="/IMG_8545.jpg" alt="Jan Lynch with family" className="photo-img" />
              </div>
            </div>
          </div>
        </section>

        <section className="sponsors" aria-labelledby="sponsors-heading">
          <div className="container sponsors-inner">
            <h2 id="sponsors-heading" className="section-title">Proceeds Benefit</h2>
            <div className="sponsors-grid">
              <div className="sponsor-card" onClick={() => window.open('https://lightways.org/hospice-care/', '_blank')}>
                <img src="/lightways.webp" alt="Lightways Hospice Care" className="sponsor-logo" />
                <div className="sponsor-info">
                  <h3>Lightways Hospice Care</h3>
                  <p>Founded as Joliet Area Community Hospice</p>
                </div>
              </div>
              <div className="sponsor-card" onClick={() => window.open('https://www.cancer.org/cancer.html', '_blank')}>
                <img src="/american_cancer_society.svg" alt="American Cancer Society" className="sponsor-logo" />
                <div className="sponsor-info">
                  <h3>American Cancer Society</h3>
                  <p>HPV Prevention, Research, and Awareness</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer" role="contentinfo">
        <div className="container footer-inner">
          <p>© {currentYear} Shut The Front Door To Cancer • The Lynch Family</p>
          <nav aria-label="Footer">
            <a
              href="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
              className="footer-link"
              zeffy-form-link="https://www.zeffy.com/embed/donation-form/donate-to-change-lives-6953?modal=true"
            >
              Donate
            </a>
            <a href="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true" className="footer-link" zeffy-form-link="https://www.zeffy.com/embed/ticketing/party-on-bourbon?modal=true">Register</a>
          </nav>
        </div>
      </footer>

    </div>
  );
}

export default App;

